/**
 * MODERATOR ALERT
 * ===============
 * Componente para mostrar alertas del moderador automático.
 * 
 * Tipos de alertas:
 * - warning: Advertencia leve (continúa sesión)
 * - critical: Sesión pausada o finalizada
 * 
 * Props:
 * - moderatorAlert: Objeto con datos de la alerta
 * - onContinue: Callback para continuar sesión (solo en pause)
 * - onRequestHelp: Callback para solicitar ayuda del coach
 */

import React, { useMemo, memo } from 'react';
import { ShieldAlert, ShieldX, Lightbulb, X } from 'lucide-react';

const ModeratorAlert = memo(({ moderatorAlert, onContinue, onRequestHelp, onClose, onDismiss }) => {
  // 🛡️ [DEFENSA] Fallback para desenrollar posible anidación residual
  const alert = moderatorAlert?.moderatorAlert ?? moderatorAlert;
  
  // 🛡️ [AUDIT] Log del prop recibido (PUNTO 3 de auditoría)
  console.log('[MOD_ALERT_PROP_RECEIVED]', {
    received_prop: moderatorAlert,
    after_fallback: alert,
    active: alert?.active,
    action: alert?.action,
    message: alert?.message,
    timestamp: Date.now()
  });

  if (!alert || !alert.active) {
    console.log('🛡️ [MODERACION_IA][ModeratorAlert] ❌ No renderizar - moderatorAlert inactivo');
    return null;
  }

  console.log('🛡️ [MODERACION_IA][ModeratorAlert] ✅ Renderizando modal de moderación');

  const { message, action, suggestHelp } = alert;

  const isWarn = action === 'warn';
  const isBlocked = action === 'show_modal' || action === 'pause_session';
  const isEndSession = action === 'end_session';
  const isPause = action === 'pause_session';

  // 🎟️ TCK-P1-FE-UX-MOD-WARN-CLEAN-COPY-V4: Copy exacto y CTA honesto
  const warnTitle = 'Aviso de coherencia';
  const warnBody = 'Tu respuesta no se conectó claramente con la pregunta anterior. Intenta reformularla para responder de forma más directa.';
  const warnSecondary = 'Puedes continuar escribiendo normalmente.';

  const titleText = useMemo(() => {
    if (isEndSession) return 'Sesión finalizada';
    if (isPause) return 'Sesión pausada';
    if (isBlocked) return 'Mensaje bloqueado';
    if (isWarn) return warnTitle;
    return 'Aviso del sistema';
  }, [isEndSession, isPause, isBlocked, isWarn]);

  const bodyText = useMemo(() => {
    if (isWarn) return warnBody;
    return message;
  }, [isWarn, warnBody, message]);

  // Estilos según severidad
  const getStyles = () => {
    if (isEndSession) {
      return {
        container: 'bg-red-50 border-2 border-red-500',
        icon: 'text-red-600',
        title: 'text-red-800',
        text: 'text-red-700',
        IconComponent: ShieldX
      };
    } else if (isPause) {
      return {
        container: 'bg-red-50 border-2 border-red-500',
        icon: 'text-red-600',
        title: 'text-red-800',
        text: 'text-red-700',
        IconComponent: ShieldX
      };
    } else if (isWarn) {
      return {
        container: 'bg-orange-50 border-l-4 border-orange-400',
        icon: 'text-orange-600',
        title: 'text-orange-900',
        text: 'text-orange-800',
        IconComponent: Lightbulb
      };
    } else {
      return {
        container: 'bg-orange-50 border-l-4 border-orange-500',
        icon: 'text-orange-600',
        title: 'text-orange-800',
        text: 'text-orange-700',
        IconComponent: ShieldAlert
      };
    }
  };

  const styles = getStyles();
  const Icon = styles.IconComponent;

  return (
    <div className={`${styles.container} px-4 py-2 rounded-lg shadow-sm mb-2 animate-fade-in`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-6 h-6 ${styles.icon} flex-shrink-0 mt-0.5`} />
        
        <div className="flex-1">
          {/* Título */}
          <p className={`font-bold ${styles.title} text-sm uppercase tracking-wide`}>
            {titleText}
          </p>

          {/* Mensaje */}
          <p className={`${styles.text} text-sm mt-2 leading-relaxed`}>
            {bodyText}
          </p>

          {isWarn && (
            <p className={`${styles.text} text-sm mt-2 leading-relaxed`}>
              {warnSecondary}
            </p>
          )}

          {/* Botones de acción */}
          {isWarn && (
            <div className="flex flex-wrap gap-2 mt-4">
              <button
                onClick={() => {
                  if (typeof onDismiss === 'function') {
                    onDismiss();
                  }
                }}
                className="bg-white hover:bg-orange-50 text-orange-900 px-4 py-2 rounded text-sm transition-colors border border-orange-200"
              >
                Entendido
              </button>
            </div>
          )}

          {action === 'pause_session' && (
            <div className="flex gap-2 mt-4">
              {onContinue && (
                <button
                  onClick={onContinue}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded text-sm transition-colors flex items-center gap-2"
                >
                  Continuar sesión
                </button>
              )}
              {onClose && (
                <button
                  onClick={onClose}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded text-sm transition-colors flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Cerrar sesión
                </button>
              )}
            </div>
          )}

          {action === 'end_session' && (
            <div className="mt-4">
              <button
                onClick={() => window.location.reload()}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded text-sm"
              >
                Volver al inicio
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

// Nombre para debugging
ModeratorAlert.displayName = 'ModeratorAlert';

export default ModeratorAlert;
